import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Typography, Box, IconButton, Grid, Button } from '@mui/material'; // Button Added
import { Link } from 'react-router-dom'; // Link Added
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Cards from '../components/Cards/Cards';
import HeroSection from '../components/HeroSection/HeroSection';
import UserCard from '../components/UserCard/UserCard';
import { fetchUserDetails } from '../features/auth/authAction';
import { getPaidList } from '../features/cartAction';



function Home() {
  const sliderRef = useRef(null);

  // Labels
  const labels = {
    si: {
      heading: 'ඔබගේ ජීවින සහකරු මෙහි සොයා ගන්න !',
      findBride: 'ඔබගේ මනාලිය මෙහි සොයා ගන්න !',
      findGroom: 'ඔබගේ මනාලයා මෙහි සොයා ගන්න !',
      findPartner: 'ඔබගේ හොඳම ගැලපෙන සහකරු සොයා ගන්න',
      bestPlatform: 'ඔබගේ ජීවිත ගමන සඳහා හොඳම වේදිකාව මෙයයි',
      weddingPlans: 'හොඳම විවාහ සැලසුම්',
      dayFinderTitle: 'සුබ නැකැත් සොයාගන්න',
      dayFinderDesc: 'ඔබගේ විවාහ මංගල්‍යය සඳහා ඉතාමත් සුබ දිනයක් සහ වේලාවක් අපගේ ස්වයංක්‍රීය පද්ධතිය හරහා සොයාගන්න.',
      dayFinderBtn: 'දැනගන්න',
    },
    en: {
      heading: 'Find your life partner here!',
      findBride: 'Find your bride here!',
      findGroom: 'Find your groom here!',
      findPartner: 'Find your best matching partner',
      bestPlatform: 'This is the best platform for your life journey',
      weddingPlans: 'Best wedding plans',
      dayFinderTitle: 'Find Your Auspicious Date',
      dayFinderDesc: 'Discover the perfect auspicious time and date for your wedding ceremony using our advanced astrological calculator.',
      dayFinderBtn: 'Check Now',
    }
  };
  const [lang, setLang] = useState(() => localStorage.getItem('language') || 'si');

  const t = labels[lang] ?? labels.si;
  const dispatch = useDispatch();
  const { usersInfo, cacheTimestamp, loading, error } = useSelector((state) => state.user);
  const loggedUser = useSelector((state) => state.auth.profileInfo?.user);
  const paidUserList = useSelector((state) => state.cartDetails?.paidUserList);

  useEffect(() => {
    // Cache duration: 30 minutes
    const CACHE_DURATION = 30 * 60 * 1000;
    const isCacheFresh = cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION);

    // Only fetch if cache is stale or missing
    if (!isCacheFresh || !usersInfo) {
      console.log('Fetching user details from API (cache stale or missing)');
      dispatch(fetchUserDetails());
    } else {
      console.log('Using cached user details (cache is fresh)');
    }

    if (loggedUser) {
      dispatch(getPaidList(loggedUser.id));
    }
  }, [dispatch, cacheTimestamp, usersInfo, loggedUser]);

  // Auto-scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (sliderRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
        // Check if we've reached the end (approximate)
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          // Reset to start
          sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          // Scroll next
          sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        }
      }
    }, 3000); // Scroll every 3 seconds

    return () => clearInterval(interval);
  }, []);

  const users = Array.isArray(usersInfo) ? usersInfo : usersInfo?.userData || usersInfo?.data || [];

  // Sort users: Users with images first
  const sortedUsers = [...users].sort((a, b) => {
    const aHasImage = (a.images && a.images.length > 0) || a.image || a.profilePic || (a.imageCount > 0);
    const bHasImage = (b.images && b.images.length > 0) || b.image || b.profilePic || (b.imageCount > 0);

    if (aHasImage && !bHasImage) return -1;
    if (!aHasImage && bHasImage) return 1;
    return 0;
  });

  const isPaidUser = paidUserList?.data?.length > 0;

  console.log('DEBUG: Home.js state.details:', { usersInfo, loading, error, users: sortedUsers });
  return (
    <>
      <HeroSection />
      <Cards />
      {/* Available Profiles Section */}
      <Container maxWidth="xl" sx={{ my: 8 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'text.primary' }}>
          Available Profiles
        </Typography>

        {/* Carousel Container */}
        <Box sx={{ position: 'relative' }}>
          {/* Left Button */}
          <IconButton
            onClick={() => {
              if (sliderRef.current) sliderRef.current.scrollBy({ left: -300, behavior: 'smooth' });
            }}
            sx={{
              position: 'absolute', left: -20, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
              bgcolor: 'background.paper', boxShadow: 3, '&:hover': { bgcolor: 'grey.100' },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Right Button */}
          <IconButton
            onClick={() => {
              if (sliderRef.current) sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
            }}
            sx={{
              position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
              bgcolor: 'background.paper', boxShadow: 3, '&:hover': { bgcolor: 'grey.100' },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowForwardIcon />
          </IconButton>

          {/* Horizontal Slider Container */}
          <Box
            ref={sliderRef}
            id="user-slider-container"
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 3,
              py: 4,
              px: 2,
              scrollBehavior: 'smooth',
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
                borderRadius: '10px',
              },
            }}
          >
            {loading ? (
              <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Loading profiles...
                </Typography>
              </Box>
            ) : sortedUsers && sortedUsers.length > 0 ? (
              sortedUsers.map((user) => (
                <Box
                  key={user.userId || user.id}
                  sx={{
                    minWidth: { xs: '85%', sm: '350px', md: '300px' },
                    scrollSnapAlign: 'start',
                    flexShrink: 0
                  }}
                >
                  <UserCard user={user} isPaidUser={isPaidUser} />
                </Box>
              ))
            ) : (
              <Box sx={{ width: '100%', textAlign: 'center', mt: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Loading profiles...
                  {/* null values  */}

                </Typography>
              </Box>
            )}
          </Box>

        </Box>

        {/* Feature Highlight Section (Day Finder) */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #054A49 0%, #0a6967 100%)',
            color: 'white',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            mt: 8,
            mb: 6,
            boxShadow: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center'
          }}
        >
          {/* Text Content */}
          <Box sx={{ p: { xs: 4, md: 8 }, flex: 1, zIndex: 2 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 800, fontFamily: "'Playfair Display', serif" }}>
              {t.dayFinderTitle}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 4, maxWidth: '600px', fontWeight: 300 }}>
              {t.dayFinderDesc}
            </Typography>
            <Button
              component={Link}
              to="/day-finder"
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                borderRadius: 50,
                boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                '&:hover': { transform: 'scale(1.05)' }
              }}
            >
              {t.dayFinderBtn}
            </Button>
          </Box>

          {/* Decorative Image/Pattern */}
          <Box
            sx={{
              flex: 1,
              height: '100%',
              minHeight: { xs: 200, md: 400 },
              width: '100%',
              position: 'relative',
              backgroundImage: 'url("images/img5.jpg")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, bottom: 0, right: 0,
                background: 'linear-gradient(to right, #054A49 0%, rgba(5, 74, 73, 0.4) 50%, transparent 100%)',
                zIndex: 1
              }
            }}
          />
        </Box>


      </Container>
    </>
  );
}

export default Home;